import React, { useEffect, useState } from 'react';
import { Plus, Search, Eye, Trash2, ToggleLeft, ToggleRight } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';
import StudentForm from '../components/StudentForm';
import StudentDetail from '../components/StudentDetail';

export default function Students() {
  const [students, setStudents] = useState([]);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null); // student object

  const load = (q = '') => api.get('/students', { params: { search: q } }).then(r => setStudents(r.data)).catch(console.error);

  const toggleStatus = async (s) => {
    const newStatus = s.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
    try {
      await api.put(`/students/${s.id}/status`, { status: newStatus });
      toast.success(`${s.firstName} ${s.lastName} set to ${newStatus.toLowerCase()}`);
      load(search);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error');
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/students/${confirmDelete.id}`);
      toast.success(`${confirmDelete.firstName} ${confirmDelete.lastName} deleted`);
      setConfirmDelete(null);
      load(search);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error');
      setConfirmDelete(null);
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { const t = setTimeout(() => load(search), 400); return () => clearTimeout(t); }, [search]);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h2 className="text-xl font-bold text-gray-800">Students</h2>
        <button onClick={() => setShowForm(true)} className="bg-brand-700 hover:bg-brand-800 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2">
          <Plus size={16} /> Register Student
        </button>
      </div>

      <div className="relative mb-4">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, reg no, phone, NIC..."
          className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
              <tr>
                <th className="px-4 py-3 text-left">Reg No</th>
                <th className="px-4 py-3 text-left">Name</th>
                <th className="px-4 py-3 text-left">Phone</th>
                <th className="px-4 py-3 text-left">Room</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-left"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {students.map(s => (
                <tr key={s.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono text-brand-700">{s.registrationNo}</td>
                  <td className="px-4 py-3 font-medium">{s.firstName} {s.lastName}</td>
                  <td className="px-4 py-3 text-gray-500">{s.phone}</td>
                  <td className="px-4 py-3 text-gray-500">{s.tenancies?.[0]?.room?.roomNumber || '-'}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${s.status === 'ACTIVE' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                      {s.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <button onClick={() => setSelected(s.id)} className="text-brand-600 hover:text-brand-800" title="View details">
                        <Eye size={16} />
                      </button>
                      <button
                        onClick={() => toggleStatus(s)}
                        title={s.status === 'ACTIVE' ? 'Deactivate student' : 'Activate student'}
                        className={`transition-colors ${s.status === 'ACTIVE' ? 'text-green-600 hover:text-gray-400' : 'text-gray-300 hover:text-green-600'}`}>
                        {s.status === 'ACTIVE'
                          ? <ToggleRight size={18} />
                          : <ToggleLeft size={18} />}
                      </button>
                      <button onClick={() => setConfirmDelete(s)} className="text-gray-300 hover:text-red-600 transition-colors" title="Delete student">
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {students.length === 0 && <p className="text-center text-gray-400 py-10">No students found</p>}
        </div>
      </div>

      {showForm && <StudentForm onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
      {selected && <StudentDetail studentId={selected} onClose={() => setSelected(null)} onUpdated={load} />}

      {/* ── Confirm Delete Student ── */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-sm shadow-xl">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <Trash2 size={18} className="text-red-600" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900">Delete Student?</h3>
                <p className="text-sm text-gray-500 mt-1">
                  <strong>{confirmDelete.firstName} {confirmDelete.lastName}</strong>{' '}
                  ({confirmDelete.registrationNo}) will be permanently removed.
                </p>
              </div>
            </div>
            <div className="bg-red-50 border border-red-100 rounded-lg px-3 py-2 text-xs text-red-700 mb-4">
              ⚠️ All linked records (tenancy history, payments, reservations, laundry) will also be deleted. This cannot be undone.
            </div>
            {confirmDelete.status === 'ACTIVE' && (
              <div className="bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 text-xs text-amber-700 mb-4">
                ℹ️ If this student has an active tenancy the server will block the deletion — end the tenancy first.
              </div>
            )}
            <div className="flex gap-3">
              <button onClick={() => setConfirmDelete(null)}
                className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg text-sm hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={doDelete}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 rounded-lg text-sm font-medium">
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
